import { Box, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Check } from '@mui/icons-material';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

const ProgressIndicator = ({ currentStep, totalSteps }: ProgressIndicatorProps) => {
  if (totalSteps <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '100px',
        padding: '12px 24px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {Array.from({ length: totalSteps }, (_, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const stepNumber = index + 1;

          return (
            <Stack key={index} direction="row" alignItems="center" spacing={2}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1, type: 'spring', stiffness: 500, damping: 30 }}
              >
                <Box
                  sx={{
                    width: isCurrent ? 40 : 32,
                    height: isCurrent ? 40 : 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      isCompleted || isCurrent
                        ? 'linear-gradient(135deg, #2B9FFF 0%, #667eea 100%)'
                        : 'rgba(0, 0, 0, 0.1)',
                    color: isCompleted || isCurrent ? '#fff' : 'rgba(0, 0, 0, 0.3)',
                    fontWeight: 600,
                    fontSize: isCurrent ? '16px' : '14px',
                    transition: 'all 0.3s ease',
                    boxShadow: isCurrent ? '0 4px 15px rgba(43, 159, 255, 0.4)' : 'none',
                    position: 'relative',
                  }}
                >
                  {isCompleted ? (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                    >
                      <Check sx={{ fontSize: '18px' }} />
                    </motion.div>
                  ) : (
                    <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 1 }}>
                      {stepNumber}
                    </Typography>
                  )}

                  {/* Pulse animation for current step */}
                  {isCurrent && (
                    <motion.div
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.5, 0, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2B9FFF 0%, #667eea 100%)',
                      }}
                    />
                  )}
                </Box>
              </motion.div>

              {/* Connecting line */}
              {index < totalSteps - 1 && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: index * 0.1 + 0.2, duration: 0.3 }}
                  style={{ transformOrigin: 'left' }}
                >
                  <Box
                    sx={{
                      width: { xs: '20px', sm: '40px', md: '60px' },
                      height: '3px',
                      borderRadius: '2px',
                      background: isCompleted
                        ? 'linear-gradient(90deg, #2B9FFF 0%, #667eea 100%)'
                        : 'rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.5s ease',
                    }}
                  />
                </motion.div>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ProgressIndicator;
